import { useMutation, useQuery } from "@tanstack/react-query";
import {
  deleteSuggestedById,
  searchSuggested,
  importSuggestedFile,
  createUserSuggested,
  updateUserSuggested,
} from "../api/suggested";
import { toast } from "react-toastify";
import { handleApiError } from "../api/InstanceApi";
import { useIntl } from "react-intl";

// Hook to get suggestion data
export const useSuggestionData = (searchParams) => {
  const intl = useIntl();
  const {
    data: suggestionData,
    isLoading: suggestionDataLoading,
    error: suggestionDataError,
    isFetching: isSuggestionDataFetching,
  } = useQuery({
    queryKey: [
      "suggestion-data",
      searchParams?.page,
      searchParams?.limit,
      searchParams?.suggest,
    ],
    queryFn: () => searchSuggested(searchParams),
    staleTime: 0,
  });

  const deleteMutation = useMutation({
    mutationFn: async (id) => {
      return await toast.promise(deleteSuggestedById(id), {
        pending: {
          render() {
            return `${intl.formatMessage({ id: "AGENTS.SUGGESTION_DATA.DELETING" })}`;
          },
        },
        success: {
          render() {
            return `${intl.formatMessage({ id: "AGENTS.SUGGESTION_DATA.DELETED" })}`;
          },
        },
        error: {
          render({ data }) {
            return data.message;
          },
        },
      });
    },
    onError: (data) => handleApiError(data),
  });

  const importMutation = useMutation({
    mutationFn: async (file) => {
      const formData = new FormData();
      formData.append("file", file);
      return await toast.promise(importSuggestedFile(formData), {
        pending: {
          render() {
            return `${intl.formatMessage({ id: "AGENTS.SUGGESTION_DATA.IMPORTING" })}`;
          },
        },
        success: {
          render() {
            return `${intl.formatMessage({ id: "AGENTS.SUGGESTION_DATA.IMPORTED" })}`;
          },
        },
        error: {
          render({ data }) {
            return data.message;
          },
        },
      });
    },
    onError: (data) => handleApiError(data),
  });

  const createMutation = useMutation({
    mutationFn: async (data) => {
      return await toast.promise(createUserSuggested(data), {
        pending: {
          render() {
            return `${intl.formatMessage({ id: "AGENTS.SUGGESTION_DATA.CREATING" })}`;
          },
        },
        success: {
          render() {
            return `${intl.formatMessage({ id: "AGENTS.SUGGESTION_DATA.CREATED" })}`;
          },
        },
        error: {
          render({ data }) {
            return data.message;
          },
        },
      });
    },
    onError: (data) => handleApiError(data),
  });

  const updateMutation = useMutation({
    mutationFn: async (data) => {
      return await toast.promise(updateUserSuggested(data.id, data), {
        pending: {
          render() {
            return `${intl.formatMessage({ id: "AGENTS.SUGGESTION_DATA.UPDATING" })}`;
          },
        },
        success: {
          render() {
            return `${intl.formatMessage({ id: "AGENTS.SUGGESTION_DATA.UPDATED" })}`;
          },
        },
        error: {
          render({ data }) {
            return data.message;
          },
        },
      });
    },
    onError: (data) => handleApiError(data),
  });

  return {
    suggestionData,
    suggestionDataLoading,
    suggestionDataError,
    isSuggestionDataFetching,
    deleteSuggestedByIdAsync: deleteMutation.mutateAsync,
    importSuggestedFileAsync: importMutation.mutateAsync,
    createSuggestedAsync: createMutation.mutateAsync,
    updateSuggestedAsync: updateMutation.mutateAsync,
  };
};
